import { lazy, Suspense, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { NAV, PSEUDO_LOCALE } from '@platform/shared';
import { apiErrorMessage } from './api/errorMessage';
import { useMe } from './auth/useMe';
import { MessageScope, useLocale } from './i18n';
import { common } from './i18n/messages';
import { AppShell } from './shell/AppShell';
import { FullPage } from './shell/FullPage';
import { ComingPage } from './pages/ComingPage';
import { LegacyRedirect } from './pages/LegacyRedirect';

import { loaders } from './pages/lazy';

// Each room is its own chunk (src/pages/lazy.ts) with its messages (src/pages/namespaces.ts); the
// shell itself carries the chrome, the sign-in and hand-off pieces load when a visit needs them (§4.5).
const SignInPage = lazy(loaders.signIn);
const PulsePage = lazy(loaders.pulse);
const DecisionPage = lazy(loaders.decision);
const SessionPage = lazy(loaders.session);
const StatusPage = lazy(loaders.status);
const WorkspacePage = lazy(loaders.workspace);
const ThreadPage = lazy(loaders.thread);
const YouPage = lazy(loaders.you);
const ActivityPage = lazy(loaders.activity);
const GatePage = lazy(loaders.gate);
const FindingPage = lazy(loaders.finding);
const PlanPage = lazy(loaders.plan);
const CalendarPage = lazy(loaders.calendar);
const PlanChangePage = lazy(loaders.planChange);
const PackagePage = lazy(loaders.package);
const ReportAuthorizePage = lazy(loaders.reportAuthorize);
const DocsPage = lazy(loaders.docs);
const DocPage = lazy(loaders.doc);
const TranslationQueuePage = lazy(loaders.translationQueue);
const WireframesPage = lazy(loaders.wireframes);
const UnitsPage = lazy(loaders.units);
const UnitPage = lazy(loaders.unit);
const StoriesPage = lazy(loaders.stories);
const StoryPage = lazy(loaders.story);
const WireframePage = lazy(loaders.wireframe);
const StaleBatchPage = lazy(loaders.wireframeStale);
const BuildPage = lazy(loaders.build);
const BuildRunPage = lazy(loaders.buildRun);

export function App() {
  const me = useMe();
  const location = useLocation();
  const intl = useIntl();
  const { locale, setLocale } = useLocale();

  // people.locale is the source of truth once signed in (the pseudo-locale sweep is exempt).
  const personLocale = me.data?.person.locale;
  useEffect(() => {
    if (personLocale && locale !== PSEUDO_LOCALE && personLocale !== locale) setLocale(personLocale);
  }, [personLocale, locale, setLocale]);

  if (me.isPending) {
    return (
      <FullPage>
        <p role="status" aria-live="polite" className="muted">
          <FormattedMessage {...common.loading} />
        </p>
      </FullPage>
    );
  }

  if (me.isError) {
    return (
      <FullPage>
        <div className="card">
          <h1>
            <FormattedMessage id="app.unreachable.title" defaultMessage="The planning app is not reachable" description="Full-page error when /api/me fails" />
          </h1>
          <p className="muted">{apiErrorMessage(intl, me.error)}</p>
          <button type="button" className="btn primary" onClick={() => me.refetch()}>
            <FormattedMessage {...common.tryAgain} />
          </button>
        </div>
      </FullPage>
    );
  }

  const loading = (
    <p className="muted" role="status" aria-live="polite">
      <FormattedMessage {...common.loading} />
    </p>
  );

  if (!me.data) {
    return (
      <Routes>
        <Route path="/sign-in" element={<Suspense fallback={<FullPage>{loading}</FullPage>}><MessageScope ns="signIn"><SignInPage /></MessageScope></Suspense>} />
        <Route path="*" element={<Navigate to="/sign-in" replace state={{ from: `${location.pathname}${location.search}` }} />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/sign-in" element={<Navigate to={(location.state as { from?: string } | null)?.from ?? '/'} replace />} />
      {/* The report widget's authorize step (§3.5): a popup or a phone round trip, outside the shell. */}
      <Route path="report/authorize" element={<Suspense fallback={loading}><MessageScope ns="report"><ReportAuthorizePage me={me.data} /></MessageScope></Suspense>} />
      <Route element={<AppShell me={me.data} />}>
        <Route index element={<Suspense fallback={loading}><MessageScope ns="pulse"><PulsePage me={me.data} /></MessageScope></Suspense>} />
        {/* Object URLs under Tracks (IA §2.2, v3.8b): a decision and a session keep their paths, read under the track they belong to; /decisions and /sessions themselves redirect (LegacyRedirect, routes.ts). */}
        <Route path="decisions/:id" element={<Suspense fallback={loading}><MessageScope ns="decide"><DecisionPage me={me.data} /></MessageScope></Suspense>} />
        <Route path="sessions/:id" element={<Suspense fallback={loading}><MessageScope ns="session"><SessionPage me={me.data} /></MessageScope></Suspense>} />
        <Route path="status/:id" element={<Suspense fallback={loading}><MessageScope ns="status"><StatusPage me={me.data} /></MessageScope></Suspense>} />
        <Route path="threads" element={<Suspense fallback={loading}><MessageScope ns="workspace"><WorkspacePage /></MessageScope></Suspense>} />
        <Route path="threads/:id" element={<Suspense fallback={loading}><MessageScope ns="chat"><ThreadPage me={me.data} /></MessageScope></Suspense>} />
        <Route path="you" element={<Suspense fallback={loading}><MessageScope ns="you"><YouPage me={me.data} /></MessageScope></Suspense>} />
        <Route path="activity" element={<Suspense fallback={loading}><MessageScope ns="activity"><ActivityPage me={me.data} /></MessageScope></Suspense>} />
        <Route path="plan" element={<Suspense fallback={loading}><MessageScope ns="plan"><PlanPage me={me.data} /></MessageScope></Suspense>} />
        {/* The desk dispersed (IA §1.5, v3.8c): the gates are a Plan view and a gate page an object under it; a finding is an object URL read under the Build console, whose Findings tab is the inbox. /verify* redirects (LegacyRedirect). */}
        <Route path="plan/gates" element={<Suspense fallback={loading}><MessageScope ns="plan"><PlanPage me={me.data} view="gates" /></MessageScope></Suspense>} />
        {/* The calendar (IA §6.8, v3.10): the plan on real weeks — the desktop grid, the phone week agenda. */}
        <Route path="plan/calendar" element={<Suspense fallback={loading}><MessageScope ns="plan"><CalendarPage me={me.data} /></MessageScope></Suspense>} />
        <Route path="plan/gates/:phase" element={<Suspense fallback={loading}><MessageScope ns="plan"><GatePage me={me.data} /></MessageScope></Suspense>} />
        <Route path="plan/gates/:phase/items/:itemId" element={<Suspense fallback={loading}><MessageScope ns="plan"><GatePage me={me.data} /></MessageScope></Suspense>} />
        <Route path="plan/changes/:id" element={<Suspense fallback={loading}><MessageScope ns="plan"><PlanChangePage me={me.data} /></MessageScope></Suspense>} />
        {/* Tracks (planning-app.md §3 *Navigation from v3.8*, PA62a): the list, a track, a track's tab; /plan/units* redirects here (LegacyRedirect). */}
        <Route path="tracks" element={<Suspense fallback={loading}><MessageScope ns="track"><UnitsPage /></MessageScope></Suspense>} />
        <Route path="tracks/:id" element={<Suspense fallback={loading}><MessageScope ns="track"><UnitPage /></MessageScope></Suspense>} />
        <Route path="tracks/:id/:tab" element={<Suspense fallback={loading}><MessageScope ns="track"><UnitPage /></MessageScope></Suspense>} />
        {/* User Stories (IA §3, v3.9a): the list and a story's page. */}
        <Route path="stories" element={<Suspense fallback={loading}><MessageScope ns="stories"><StoriesPage /></MessageScope></Suspense>} />
        <Route path="stories/:id" element={<Suspense fallback={loading}><MessageScope ns="stories"><StoryPage /></MessageScope></Suspense>} />
        {/* Nav items whose package has not shipped render the placeholder naming it (none since v3.9a; the loop stays for the next item). */}
        {NAV.filter((n) => !n.shipped).map((n) => (
          <Route key={n.id} path={`${n.path.slice(1)}/*`} element={<ComingPage item={n} />} />
        ))}
        <Route path="packages/:id" element={<Suspense fallback={loading}><MessageScope ns="plan"><PackagePage me={me.data} /></MessageScope></Suspense>} />
        <Route path="build" element={<Suspense fallback={loading}><MessageScope ns="build"><BuildPage me={me.data} /></MessageScope></Suspense>} />
        <Route path="build/findings" element={<Suspense fallback={loading}><MessageScope ns="build"><BuildPage me={me.data} tab="findings" /></MessageScope></Suspense>} />
        <Route path="build/runs/:id" element={<Suspense fallback={loading}><MessageScope ns="build"><BuildRunPage me={me.data} /></MessageScope></Suspense>} />
        <Route path="findings/:id" element={<Suspense fallback={loading}><MessageScope ns="build"><FindingPage me={me.data} /></MessageScope></Suspense>} />
        <Route path="docs" element={<Suspense fallback={loading}><MessageScope ns="docs"><DocsPage me={me.data} /></MessageScope></Suspense>} />
        <Route path="docs/*" element={<Suspense fallback={loading}><MessageScope ns="docs"><DocPage me={me.data} /></MessageScope></Suspense>} />
        <Route path="translations/:locale" element={<Suspense fallback={loading}><MessageScope ns="docs"><TranslationQueuePage me={me.data} /></MessageScope></Suspense>} />
        <Route path="wireframes" element={<Suspense fallback={loading}><MessageScope ns="wireframes"><WireframesPage /></MessageScope></Suspense>} />
        <Route path="wireframes/stale" element={<Suspense fallback={loading}><MessageScope ns="wireframes"><StaleBatchPage /></MessageScope></Suspense>} />
        <Route path="wireframes/:file" element={<Suspense fallback={loading}><MessageScope ns="wireframes"><WireframePage /></MessageScope></Suspense>} />
        <Route path="*" element={<LegacyRedirect />} />
      </Route>
    </Routes>
  );
}
